import { HHTextField } from '@hinge-health/react-component-library';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { CONTRACT_FORM_PACKAGE_START_DATE_PICKER_ID } from '../../../../constants/strings/contract/form-constants';
import {
  getFormErrorsForInnerField,
  innerFieldHasErrors,
} from '../../../../utils/contract-utils';
import { fieldArrayName } from '../form';

export interface PackageStartDatePickerProps {
  index: number;
  wholeFormDisabled: boolean;
}

export const PackageStartDatePicker = ({
  index,
  wholeFormDisabled,
}: PackageStartDatePickerProps): JSX.Element => {
  const {
    control,
    trigger,
    formState: { errors },
  } = useFormContext();

  const startDateName = `${fieldArrayName}.${index}.startDate` as const;
  const testId = `${CONTRACT_FORM_PACKAGE_START_DATE_PICKER_ID}-${index}`;

  // Re-validate package start dates when the contract's start/end date changes
  // to flag existing packages whose dates fall outside the new contract range.
  const [contractStartDate, contractEndDate] = useWatch({
    name: ['startDate', 'endDate'],
  });

  useEffect(() => {
    trigger(startDateName);
  }, [contractStartDate, contractEndDate, trigger, startDateName]);

  const shouldDisableDate = (selectedDate: dayjs.Dayjs | null): boolean => {
    if (!selectedDate) return false;
    if (
      contractStartDate &&
      dayjs(selectedDate).isBefore(contractStartDate, 'day')
    ) {
      return true;
    }
    if (
      contractEndDate &&
      dayjs(selectedDate).isAfter(contractEndDate, 'day')
    ) {
      return true;
    }
    return false;
  };

  return (
    <Controller
      control={control}
      name={startDateName}
      render={({ field: { onChange, value } }): JSX.Element => (
        <DatePicker
          label="Package start date"
          value={value ?? null}
          disabled={wholeFormDisabled}
          shouldDisableDate={shouldDisableDate}
          onChange={(newValue): void => {
            onChange(newValue ? dayjs(newValue).toDate() : null);
          }}
          renderInput={(params): JSX.Element => (
            <HHTextField
              data-testid={testId}
              {...params}
              error={
                params.error || innerFieldHasErrors('startDate', index, errors)
              }
              name={startDateName}
              fullWidth
              hhVariant="variant-bypass"
              helperText={getFormErrorsForInnerField(
                'startDate',
                index,
                errors,
              )}
              InputLabelProps={{ sx: { color: 'gray' } }}
            />
          )}
        />
      )}
    />
  );
};
